import { EDatasourceType } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import {
  ConnectionStringBuilderService,
  IConnectionStringParams,
  IExtraConnectionStringParam
} from "../../../services/connection-string-builder-service";
import { DatasourceService } from "../../../services/datasource-service";
import { DraftRowBlankTest, isBlankText, withoutBlankRows } from "../shared/kuika-edit-grid/draft-rows";

/** What every probe answers with, whichever endpoint it went to. */
export interface ITestConnectionResult {
  success: boolean;
  message: string;
}

/**
 * What separates one SQL connection form from another. Everything else — the sheet, the
 * mode switch, the parameter table, save, test, the unsaved-changes hash — is the same
 * code in `sql-connection-form.tsx`.
 *
 * The three forms this replaces were 97% identical after normalising the engine name, and
 * the 3% had drifted into bugs: 73 commits landed on the MSSQL copy, 19 on Oracle, 11 on
 * PostgreSQL, and only 5 touched all three.
 */
export interface ISqlConnectionField {
  /** Key inside `parsedConnectionString`, spelled exactly as the builder writes it. */
  key: string;
  label: string;
  /** Kept from the form this replaces: the E2E page objects assert on these. */
  testId: string;
  /** Omit for an optional field. */
  requiredMessage?: string;
  /** Static hint under the control — a format, a unit. */
  extra?: string;
  /** Draws a reveal toggle, and is the value the rail masks. */
  isPassword?: boolean;
}

/**
 * `build`/`parse` are wrapped rather than passed as bare statics: the service's `parse*`
 * methods call `this.normalizeConnectionStringParams`, so an unbound reference throws the
 * moment a saved connection is opened for editing.
 */
export interface ISqlConnectionEngine {
  /** Prefix for every derived test id, e.g. `postgre` — NOT always the engine's name. */
  testIdPrefix: string;
  /** Names the engine in messages: "Please enter <copyName> connection string". */
  copyName: string;
  /** Also picks the bar's glyph and its tooltip (`getEditorGlyph`). */
  datasourceType: EDatasourceType;
  endpointFields: ISqlConnectionField[];
  credentialFields: ISqlConnectionField[];
  build: (parsed: IConnectionStringParams) => string;
  parse: (connectionString: string) => IConnectionStringParams;
  /** The engine's own probe — SAP answers on a different endpoint from the SQL three. */
  test: (connectionString: string) => Promise<AxiosResponse<ITestConnectionResult>>;
  /** What CREATE leads to, for the rail's "After you create" — shown only while creating. */
  afterCreate: string;
}

/** The Parameters grid's trailing row — where the next parameter is typed. */
export const blankExtraParam = (): IExtraConnectionStringParam => ({ key: "", value: "" });

/** A parameter row nothing has been typed into: the grid's draft row, or a row cleared back to nothing. */
export const isBlankExtraParam: DraftRowBlankTest<IExtraConnectionStringParam> = (
  param: IExtraConnectionStringParam | undefined
): boolean => isBlankText(param?.key) && isBlankText(param?.value);

/**
 * The fields with only the parameters that ARE parameters. Every `build` goes through it: the
 * grid always ends on a blank row, and the builder would write that row as `=;` — a key-less pair
 * the driver refuses.
 */
export const withoutBlankExtraParams = (parsed: IConnectionStringParams): IConnectionStringParams => ({
  ...parsed,
  extraParams: withoutBlankRows(parsed?.extraParams, isBlankExtraParam)
});

/**
 * Why the key of the parameter at `index` is refused, or null. A blank row is never refused —
 * it is where the next parameter goes, and a save drops it — so the draft row is never outlined.
 * The rest are the rules the form always had: a key is required once the row holds anything,
 * may not be one of the engine's own fields (compared without case), and may not repeat.
 */
export const extraParamKeyProblem = (
  engine: ISqlConnectionEngine,
  params: IExtraConnectionStringParam[] | undefined,
  index: number
): string | null => {
  const row: IExtraConnectionStringParam | undefined = params?.[index];
  if (isBlankExtraParam(row)) return null;

  const key: string = row?.key ?? "";
  if (isBlankText(key)) return "This field is required";
  if (isReservedParameterKey(engine, key)) return "This key is reserved";

  const isUsed: boolean = (params ?? []).some(
    (param: IExtraConnectionStringParam, other: number): boolean => other !== index && param?.key === key
  );
  return isUsed ? "This key is used" : null;
};

/** The SQL three: saving reads the schema back (`sd.getTables`), so the tree fills itself. */
const SQL_AFTER_CREATE: string = "Tables are read automatically after save.";

const USER_ID_LABEL: string = "User ID";
const PASSWORD_LABEL: string = "Password";

export const MSSQL_ENGINE: ISqlConnectionEngine = {
  testIdPrefix: "mssql",
  copyName: "MSSQL",
  datasourceType: EDatasourceType.CustomDb,
  endpointFields: [
    {
      key: "data source",
      label: "Server",
      testId: "mssqlServerInput",
      requiredMessage: "Please enter MSSQL Server"
    },
    {
      key: "initial catalog",
      label: "Database",
      testId: "mssqlDatabaseInput",
      requiredMessage: "Please enter MSSQL Database"
    }
  ],
  credentialFields: [
    { key: "user id", label: USER_ID_LABEL, testId: "mssqlUserIdInput" },
    { key: "password", label: PASSWORD_LABEL, testId: "mssqlPasswordInput", isPassword: true }
  ],
  build: (parsed: IConnectionStringParams): string =>
    ConnectionStringBuilderService.buildConnectionString(withoutBlankExtraParams(parsed)),
  parse: (connectionString: string): IConnectionStringParams =>
    ConnectionStringBuilderService.parseMSSQLConnectionString(connectionString),
  test: (connectionString: string): Promise<AxiosResponse<ITestConnectionResult>> =>
    DatasourceService.TestConnection(connectionString),
  afterCreate: SQL_AFTER_CREATE
};

export const ORACLE_ENGINE: ISqlConnectionEngine = {
  testIdPrefix: "oracle",
  copyName: "Oracle",
  datasourceType: EDatasourceType.Oracle,
  endpointFields: [
    {
      key: "data source",
      label: "Host",
      testId: "oracleHostInput",
      requiredMessage: "Please enter Oracle Server"
    },
    { key: "sid", label: "SID", testId: "oracleSidInput", requiredMessage: "Please enter SID" }
  ],
  credentialFields: [
    { key: "user id", label: USER_ID_LABEL, testId: "oracleUserIdInput" },
    { key: "password", label: PASSWORD_LABEL, testId: "oraclePasswordInput", isPassword: true }
  ],
  // Oracle builds `Data Source=(DESCRIPTION=…(SID=…))`, not a flat key list.
  build: (parsed: IConnectionStringParams): string =>
    ConnectionStringBuilderService.buildOracleConnectionString(withoutBlankExtraParams(parsed)),
  parse: (connectionString: string): IConnectionStringParams =>
    ConnectionStringBuilderService.parseOracleConnectionString(connectionString),
  test: (connectionString: string): Promise<AxiosResponse<ITestConnectionResult>> =>
    DatasourceService.TestConnection(connectionString),
  afterCreate: SQL_AFTER_CREATE
};

export const POSTGRESQL_ENGINE: ISqlConnectionEngine = {
  testIdPrefix: "postgre",
  copyName: "PostgreSQL",
  datasourceType: EDatasourceType.Postgre,
  endpointFields: [
    {
      key: "Server",
      label: "Server",
      testId: "postgreServerInput",
      requiredMessage: "Please enter PostgreSQL Server"
    },
    {
      key: "Database",
      label: "Database",
      testId: "postgreDatabaseInput",
      requiredMessage: "Please enter PostgreSQL Database"
    }
  ],
  credentialFields: [
    { key: "User Id", label: USER_ID_LABEL, testId: "postgreUserIdInput" },
    { key: "password", label: PASSWORD_LABEL, testId: "postgrePasswordInput", isPassword: true }
  ],
  build: (parsed: IConnectionStringParams): string =>
    ConnectionStringBuilderService.buildConnectionString(withoutBlankExtraParams(parsed)),
  parse: (connectionString: string): IConnectionStringParams =>
    ConnectionStringBuilderService.parsePostgreSQLConnectionString(connectionString),
  test: (connectionString: string): Promise<AxiosResponse<ITestConnectionResult>> =>
    DatasourceService.TestConnection(connectionString),
  afterCreate: SQL_AFTER_CREATE
};

/**
 * SAP joins the three SQL engines because everything that separates it is expressible as
 * data: a different probe endpoint and six fields of its own. Its `extraParams` land in the connection string exactly as theirs do.
 */
export const SAP_ENGINE: ISqlConnectionEngine = {
  testIdPrefix: "sap",
  copyName: "SAP",
  datasourceType: EDatasourceType.SAP,
  endpointFields: [
    {
      key: "AppServerHost",
      label: "Application server host",
      testId: "sapAppServerHostInput",
      requiredMessage: "Please enter AppServerHost"
    },
    {
      key: "SystemNumber",
      label: "System number",
      testId: "sapSystemNumberInput",
      requiredMessage: "Please enter SystemNumber",
      extra: "Two digits, e.g. 00"
    },
    { key: "Client", label: "Client", testId: "sapClientInput" }
  ],
  credentialFields: [
    { key: "User", label: "User", testId: "sapUserInput" },
    { key: "Password", label: "Password", testId: "sapPasswordInput", isPassword: true },
    { key: "Language", label: "Language", testId: "sapLanguageInput" }
  ],
  build: (parsed: IConnectionStringParams): string =>
    ConnectionStringBuilderService.buildSAPConnectionString(withoutBlankExtraParams(parsed)),
  parse: (connectionString: string): IConnectionStringParams =>
    ConnectionStringBuilderService.parseSAPConnectionString(connectionString),
  test: (connectionString: string): Promise<AxiosResponse<ITestConnectionResult>> =>
    DatasourceService.TestSapConnection(connectionString),
  afterCreate: "Add SAP actions from its Actions group (New SAP Action)."
};

/**
 * Whether a parameter key would collide with one of the engine's own fields, compared
 * without case. Derived from the fields rather than restated, which is what fixes the two
 * hand-maintained lists this replaced: Oracle's guarded `initial catalog`, a key it does
 * not have, and left its real `sid` open; PostgreSQL's compared a lower-cased key against
 * a title-case list and so never matched anything.
 */
export const isReservedParameterKey = (engine: ISqlConnectionEngine, value: string): boolean => {
  const candidate: string = value.toLowerCase();
  return [...engine.endpointFields, ...engine.credentialFields].some(
    (field: ISqlConnectionField): boolean => field.key.toLowerCase() === candidate
  );
};
